import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, Card, DataTable, KPI } from "@/components/Primitives";
import StatusPill from "@/components/StatusPill";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, ArrowRight } from "@phosphor-icons/react";

export default function SupplierPortal() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => {
    let active = true;
    api.get("/supplier/dashboard").then((r) => { if (active) setD(r.data); }).catch(() => {});
    return () => { active = false; };
  }, []);

  if (!d) return <div className="p-8 text-zinc-500 text-xs tracking-widest uppercase">Loading secure room…</div>;
  const k = d.kpis || {};

  return (
    <div data-testid="supplier-portal">
      <PageHeader
        overline={`Secure Assignment Room · ${user?.name || ""}`}
        title="Execution Console"
        sub="You only see opportunities your organization is assigned to. Internal scores, margins, strategy and other suppliers are never shown here."
      />
      <div className="p-8 space-y-7">
        <section className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="supplier-kpis">
          <KPI label="Assigned Opportunities" value={k.assigned_opportunities ?? 0} accent />
          <KPI label="Samples Due Soon" value={k.samples_due_soon ?? 0} />
          <KPI label="Overdue Samples" value={k.overdue_samples ?? 0} hint={k.overdue_samples ? "Needs attention" : ""} />
          <KPI label="Deliverables Due Soon" value={k.deliverables_due_soon ?? 0} />
          <KPI label="Open Questions" value={k.open_questions ?? 0} />
          <KPI label="Production At Risk" value={k.production_at_risk ?? 0} hint={k.production_at_risk ? "Review readiness" : ""} />
        </section>

        <section>
          <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2 flex items-center gap-2">
            <ShieldCheck size={14} className="text-[#FFB300]" /> Your Assigned Opportunities
          </div>
          {d.assignments.length === 0 ? (
            <Card className="p-8 text-center text-zinc-500 text-xs tracking-widest uppercase">
              No assignments visible yet. The +++ team will share opportunities here when ready.
            </Card>
          ) : (
            <DataTable
              testId="supplier-assignments-table"
              columns={[
                { key: "opportunity_name", label: "Opportunity", render: (r) => (
                  <span className="text-white font-medium">{r.opportunity?.opportunity_name}</span>
                ) },
                { key: "country", label: "Country", render: (r) => r.opportunity?.country },
                { key: "product_category", label: "Category", render: (r) => r.opportunity?.product_category },
                { key: "sample_due_date", label: "Sample Due", mono: true, render: (r) => r.sample_due_date || "—" },
                { key: "assignment_status", label: "Your Status", render: (r) => <StatusPill value={r.assignment_status} /> },
                { key: "_open", label: "", render: () => (
                  <span className="inline-flex items-center gap-1 text-[#FFB300] text-[10px] tracking-widest uppercase">Open <ArrowRight size={12} /></span>
                ) },
              ]}
              rows={d.assignments}
              onRowClick={(r) => nav(`/supplier/assignments/${r.id}`)}
            />
          )}
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <OverviewSection
            title="Pending Samples"
            rows={d.samples}
            columns={[
              { key: "sample_name", label: "Sample" },
              { key: "sample_due_date", label: "Due", mono: true },
              { key: "sample_status", label: "Status", render: (r) => <StatusPill value={r.sample_status} /> },
            ]}
          />
          <OverviewSection
            title="Deliverables"
            rows={d.deliverables}
            columns={[
              { key: "deliverable_name", label: "Deliverable" },
              { key: "due_date", label: "Due", mono: true },
              { key: "status", label: "Status", render: (r) => <StatusPill value={r.status} /> },
            ]}
          />
          <OverviewSection
            title="Questions & Answers"
            rows={d.questions}
            columns={[
              { key: "question_title", label: "Topic" },
              { key: "asked_date", label: "Asked", mono: true, render: (r) => (r.asked_date || "").slice(0, 10) },
              { key: "question_status", label: "Status", render: (r) => <StatusPill value={r.question_status} /> },
            ]}
          />
          <OverviewSection
            title="Production Readiness"
            rows={d.production}
            columns={[
              { key: "production_tracker_name", label: "Tracker" },
              { key: "sample_dependency_status", label: "Sample Dep.", render: (r) => <StatusPill value={r.sample_dependency_status} /> },
              { key: "production_status", label: "Status", render: (r) => <StatusPill value={r.production_status} /> },
            ]}
          />
        </div>

        <div className="text-[11px] text-zinc-600 tracking-tight pt-2">
          Open an assignment above to update samples, deliverables, questions and production readiness.
        </div>
      </div>
    </div>
  );
}

function OverviewSection({ title, rows, columns }) {
  return (
    <section>
      <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">{title}</div>
      <DataTable columns={columns} rows={rows || []} emptyText="Nothing here yet" />
    </section>
  );
}
